import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound, setSoundProfile } from '../lib/soundEffects';
import { generateAsciiBanner } from '../lib/asciiBannerGenerator';
import { getCircadianPhase } from '../lib/ambientChronos';

const { FiTerminal, FiX, FiCornerDownLeft } = FiIcons;

export default function CyberTerminalModal({
  isOpen,
  onClose,
  data = {},
  onSearch,
  onOpenBookmark,
  onSelectTag,
  onToggleZen,
  onOpenVault,
  onOpenReading,
  onOpenGraph,
  onOpenScratchpad,
  onExportObsidian,
  onExportHtml,
  onOpenAudit,
  onSetDensity,
  onOpenCodeRunner,
  onArchiveStale,
  onOpenHeatmap,
  onOpenTotp,
  onOpenRegexRules,
  onOpenPdfViewer,
  onOpenSnapshot,
  themeColors: tc = {},
}) {
  const [inputVal, setInputVal] = useState('');
  const [history, setHistory] = useState([
    { type: 'sys', text: 'MeMark Cyber Terminal CLI v2.5 • Type :help for command matrix.' },
  ]);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 80);
      playSound('click');
    }
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  if (!isOpen) return null;

  const handleCommand = (cmdStr) => {
    const raw = cmdStr.trim();
    if (!raw) return;

    const parts = raw.replace(/^:/, '').split(' ');
    const cmd = parts[0].toLowerCase();
    const arg = parts.slice(1).join(' ').trim();

    const newLogs = [...history, { type: 'cmd', text: `> ${raw}` }];

    switch (cmd) {
      case 'help':
        newLogs.push({
          type: 'res',
          text: [
            'AVAILABLE COMMANDS:',
            '  :find <term>        - Filter library bookmarks in real-time',
            '  :open <query>       - Find and open matching link in browser tab',
            '  :tag <tag_name>     - Filter view by tag (e.g. :tag dev)',
            '  :graph              - Open Interactive 2D Spatial Constellation Mindmap (Alt+G)',
            '  :notes              - Open Sovereign Markdown Scratchpad (Alt+N)',
            '  :export <obsidian|html> - Download library as Obsidian Vault or Single-File Offline HTML Vault',
            '  :soundpack <theme>  - Switch audio theme (cyberpunk, arcade, quantum, stealth)',
            '  :density <mode>     - Set grid density (compact, standard, cinematic)',
            '  :ascii <text>       - Generate 3-row cyberpunk phosphor ASCII art banner',
            '  :code               - Open JavaScript sandbox code snippet runner',
            '  :archive stale      - Scan and flag stale inactive links with #archive',
            '  :heatmap            - Open 52-Week Cognitive Activity Horizon Heatmap',
            '  :totp               - Open 2FA Rolling TOTP Token Authenticator',
            '  :ascii <text>       - Generate 3-row cyberpunk phosphor ASCII art banner',
            '  :code               - Open JavaScript sandbox code snippet runner',
            '  :archive stale      - Scan and flag stale inactive links with #archive',
            '  :heatmap            - Open 52-Week Cognitive Activity Horizon Heatmap',
            '  :totp               - Open 2FA Rolling TOTP Token Authenticator',
            '  :route              - Open Regex Bookmark Auto-Routing Engine',
            '  :pdf                - Open Sovereign In-Browser PDF Reader & Annotation Sidecar',
            '  :snapshot           - Open Tab Session Snapshotter & Instant Restorer',
            '  :chronos            - Display Adaptive Circadian Illumination Status & Phase',
            '  :zen                - Toggle Zen Focus Mode',
            '  :vault              - Open Sovereign Zero-Knowledge Vault',
            '  :reading            - Open Reading Queue Drawer with Audio Narrator',
            '  :audit              - Run Library Health & Sentinel Audit',
            '  :stats              - Display library size, hits, and cipher telemetry',
            '  :ping <domain>      - Measure response latency to destination host',
            '  :clear              - Clear terminal output history',
            '  :exit               - Close terminal CLI',
          ].join('\n'),
        });
        playSound('click');
        break;

      case 'heatmap':
      case 'activity':
        if (onOpenHeatmap) {
          onOpenHeatmap();
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Opening Activity Heatmap...' });
        }
        break;

      case 'totp':
      case '2fa':
      case 'otp':
        if (onOpenTotp) {
          onOpenTotp();
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Opening 2FA TOTP Authenticator...' });
        }
        break;

      case 'route':
      case 'regex':
      case 'rules':
        if (onOpenRegexRules) {
          onOpenRegexRules();
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Opening Regex Bookmark Routing Rules...' });
        }
        break;

      case 'pdf':
        if (onOpenPdfViewer) {
          onOpenPdfViewer();
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Opening PDF Document Reader...' });
        }
        break;

      case 'snapshot':
      case 'session':
        if (onOpenSnapshot) {
          onOpenSnapshot();
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Opening Tab Session Snapshotter...' });
        }
        break;

      case 'chronos':
      case 'circadian': {
        const ph = getCircadianPhase();
        newLogs.push({
          type: 'res',
          text: `[CIRCADIAN CHRONOS HUD]\nPhase: ${ph.icon} ${ph.name}\nAccent: ${ph.accent}\nDescription: ${ph.description}\nIllumination Profile: Active`,
        });
        playSound('click');
        break;
      }

      case 'ascii': {
        const text = arg || 'MEMARK';
        const banner = generateAsciiBanner(text, 14);
        newLogs.push({ type: 'res', text: banner });
        playSound('launch');
        break;
      }

      case 'code':
      case 'sandbox':
        if (onOpenCodeRunner) {
          onOpenCodeRunner('// Sovereign JS Sandbox\nconsole.log("Terminal invoked sandbox ready.");', 'Terminal Sandbox');
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Opening Code Sandbox...' });
        }
        break;

      case 'archive': {
        if (arg.toLowerCase() === 'stale') {
          if (onArchiveStale) {
            const count = onArchiveStale();
            newLogs.push({ type: 'res', text: `Stale link sentinel scan complete. Processed ${count || 0} candidate links.` });
            playSound('launch');
          } else {
            newLogs.push({ type: 'res', text: 'Scanning and tagging stale inactive links with #archive...' });
          }
        } else {
          newLogs.push({ type: 'err', text: 'Usage: :archive stale' });
        }
        break;
      }

      case 'soundpack':
      case 'audio': {
        const theme = arg.toLowerCase();
        if (['cyberpunk', 'arcade', 'quantum', 'stealth', 'crt'].includes(theme)) {
          setSoundProfile(theme);
          playSound('launch');
          newLogs.push({ type: 'res', text: `Soundpack switched to: [${theme}]. Procedural Web Audio active.` });
        } else {
          newLogs.push({ type: 'err', text: 'Usage: :soundpack <cyberpunk | arcade | quantum | stealth | crt>' });
        }
        break;
      }

      case 'density': {
        const mode = arg.toLowerCase();
        if (['compact', 'standard', 'cinematic'].includes(mode)) {
          if (onSetDensity) onSetDensity(mode);
          localStorage.setItem('memark_grid_density', mode);
          newLogs.push({ type: 'res', text: `Grid density calibrated to: [${mode}].` });
          playSound('click');
        } else {
          newLogs.push({ type: 'err', text: 'Usage: :density <compact | standard | cinematic>' });
        }
        break;
      }

      case 'graph':
      case 'mindmap':
        if (onOpenGraph) {
          onOpenGraph();
          onClose();
        }
        break;

      case 'notes':
      case 'scratchpad':
        if (onOpenScratchpad) {
          onOpenScratchpad();
          onClose();
        }
        break;

      case 'audit':
      case 'health':
        if (onOpenAudit) {
          onOpenAudit();
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Opening Library Health & Sentinel Audit...' });
        }
        break;

      case 'export':
        if (arg.toLowerCase() === 'obsidian' || arg.toLowerCase() === 'vault') {
          if (onExportObsidian) {
            onExportObsidian();
            newLogs.push({ type: 'res', text: 'Obsidian Markdown Vault generated and download triggered.' });
            playSound('launch');
          }
        } else if (arg.toLowerCase() === 'html' || arg.toLowerCase() === 'standalone') {
          if (onExportHtml) {
            onExportHtml();
            newLogs.push({ type: 'res', text: 'Air-Gap Offline HTML Vault generated and download triggered.' });
            playSound('launch');
          }
        } else {
          newLogs.push({ type: 'err', text: 'Usage: :export <obsidian | html>' });
        }
        break;

      case 'find':
      case 'search':
        if (!arg) {
          newLogs.push({ type: 'err', text: 'Error: Please specify search term (e.g. :find react)' });
        } else {
          onSearch(arg);
          newLogs.push({ type: 'res', text: `Filtering bookmarks for: "${arg}"` });
          playSound('click');
        }
        break;

      case 'open':
        if (!arg) {
          newLogs.push({ type: 'err', text: 'Error: Please specify link title or domain (e.g. :open github)' });
        } else {
          const allLinks = Object.values(data).flatMap((f) => f.items || []).filter((i) => i.type === 'link');
          const q = arg.toLowerCase();
          const match = allLinks.find((i) => (i.title || '').toLowerCase().includes(q) || (i.url || '').toLowerCase().includes(q));
          if (match && match.url) {
            newLogs.push({ type: 'res', text: `Opening [${match.title}] -> ${match.url}` });
            onOpenBookmark(match.url, match);
            playSound('launch');
          } else {
            newLogs.push({ type: 'err', text: `No bookmark found matching: "${arg}"` });
          }
        }
        break;

      case 'tag':
        if (!arg) {
          onSelectTag(null);
          newLogs.push({ type: 'res', text: 'Tag filter cleared.' });
        } else {
          const cleanTag = arg.replace(/^#/, '');
          onSelectTag(cleanTag);
          newLogs.push({ type: 'res', text: `Active tag filter: #${cleanTag}` });
          playSound('click');
        }
        break;

      case 'zen':
        onToggleZen();
        newLogs.push({ type: 'res', text: 'Zen Focus Mode toggled.' });
        playSound('click');
        break;

      case 'vault':
        onOpenVault();
        onClose();
        break;

      case 'sentinel':
        if (onOpenAudit) {
          onOpenAudit();
          onClose();
        } else {
          newLogs.push({ type: 'res', text: 'Running Sentinel Security & Duplicate Audit...' });
        }
        break;

      case 'reading':
        onOpenReading();
        onClose();
        break;

      case 'stats': {
        const allItems = Object.values(data).flatMap((f) => f.items || []);
        const totalLinks = allItems.filter((i) => i.type === 'link').length;
        const totalFolders = Object.keys(data).length;
        const encryptedCount = allItems.filter((i) => i.credentials && i.credentials.trim()).length;
        newLogs.push({
          type: 'res',
          text: [
            '=== MEMARK SOVEREIGN TELEMETRY ===',
            `Total Bookmarks   : ${totalLinks}`,
            `Total Folders     : ${totalFolders}`,
            `Encrypted Records : ${encryptedCount} (AES-GCM-256 PBKDF2)`,
            `Storage Engine    : Sovereign Cloud + Client Storage`,
            `Zero Telemetry    : Enforced (No external tracker beacons)`,
          ].join('\n'),
        });
        playSound('lock');
        break;
      }

      case 'ping': {
        const target = arg || 'app.memark.me';
        const start = performance.now();
        const img = new Image();
        img.onload = img.onerror = () => {
          const lat = Math.round(performance.now() - start);
          setHistory((prev) => [
            ...prev,
            { type: 'res', text: `PING ${target} -> edge response received in ${lat}ms (200 OK)` },
          ]);
        };
        img.src = `https://${target.replace(/^https?:\/\//, '')}/favicon.ico?_=${Date.now()}`;
        newLogs.push({ type: 'res', text: `Dispatching HTTP ping packet to: ${target}...` });
        break;
      }

      case 'clear':
        setHistory([]);
        setInputVal('');
        return;

      case 'exit':
      case 'quit':
        onClose();
        return;

      default:
        newLogs.push({
          type: 'err',
          text: `Command not recognized: "${cmd}". Type :help for available commands.`,
        });
    }

    setHistory(newLogs);
    setInputVal('');
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          className="w-full max-w-2xl bg-[#08080c] border border-emerald-500/40 rounded-2xl shadow-[0_0_40px_rgba(16,185,129,0.15)] flex flex-col overflow-hidden"
          style={{ height: '480px' }}
        >
          {/* Top Bar */}
          <div className="px-4 py-3 bg-[#0d0d14] border-b border-[#1f1f2e] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
              <span className="ml-2 font-mono text-xs text-emerald-400 font-bold flex items-center gap-1.5">
                <SafeIcon icon={FiTerminal} className="w-3.5 h-3.5" />
                <span>memark-cli ~ sovereign-sh</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-white/40">ESC to exit</span>
              <button
                onClick={onClose}
                className="p-1 rounded-md text-white/50 hover:text-white hover:bg-white/10"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Terminal Output Stream */}
          <div className="flex-1 p-4 font-mono text-xs overflow-y-auto space-y-2 select-text">
            {history.map((h, i) => (
              <div
                key={i}
                className={`whitespace-pre-wrap leading-relaxed ${
                  h.type === 'cmd'
                    ? 'text-emerald-400 font-bold'
                    : h.type === 'err'
                    ? 'text-rose-400'
                    : h.type === 'sys'
                    ? 'text-purple-300 opacity-80'
                    : 'text-white/80'
                }`}
              >
                {h.text}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Command Prompt Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCommand(inputVal);
            }}
            className="p-3 bg-[#0c0c12] border-t border-[#1a1a24] flex items-center gap-2"
          >
            <span className="font-mono text-xs text-emerald-400 font-bold select-none">&gt;</span>
            <input
              ref={inputRef}
              type="text"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              placeholder="Type command (e.g. :help, :soundpack arcade, :density compact, :export obsidian)..."
              className="flex-1 bg-transparent font-mono text-xs text-emerald-300 focus:outline-none placeholder:text-white/30"
            />
            <button
              type="submit"
              className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 text-[10px] font-mono flex items-center gap-1"
            >
              <span>EXEC</span>
              <SafeIcon icon={FiCornerDownLeft} className="w-2.5 h-2.5" />
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
